// SPDX-License-Identifier: Elastic-2.0
/**
 * SensorView — read-only big-number readout for a `sensor` device.
 *
 * Per task spec § 7: large value + unit + last-updated time. v0.1 omits
 * the optional spark chart (flagged for v0.2).
 *
 * Robust against missing fields: shows "—" when no numeric/string value
 * is present, hides the unit when absent.
 */
import type { Device } from "@/api/gateway";

import styles from "../DeviceModal.module.css";

export interface SensorViewProps {
  device: Device;
}

function formatRelative(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function SensorView({ device }: SensorViewProps): JSX.Element {
  const state = device.state ?? {};
  let value: string | number = "—";
  if (typeof state.value === "number" || typeof state.value === "string") {
    value = state.value;
  } else if (typeof state.state === "number" || typeof state.state === "string") {
    value = state.state;
  }
  const unit = typeof state.unit === "string" ? state.unit : "";
  const relative = formatRelative(device.last_changed_at);

  return (
    <div className={styles.sensorView} data-testid="sensor-view">
      <div className={styles.sensorValueRow}>
        <span className={styles.sensorValue}>{value}</span>
        {unit ? <span className={styles.sensorUnit}>{unit}</span> : null}
      </div>
      {relative ? (
        <div className={styles.sensorMeta} aria-label="Last updated">
          Updated {relative}
        </div>
      ) : null}
    </div>
  );
}

export default SensorView;
